import _ from 'lodash';
import React from 'react';
import { Link, navigate } from 'gatsby';
import Headroom from 'react-headroom';
import AccountContext from '../../../context/account/account.context';
import { getLinkFragment } from '../../../lib/link-fragment/link-fragment';
import { StoryblokCommon } from '../../../lib/storyblok/storyblok';
import { NavigationItemTopStoryblok, NavigationLinkStoryblok, NavigationSubLinkStoryblok } from '../../../_shared/interfaces/storyblok';

import * as TopNavigationStylesHs from './top-navigation.hs.module.scss';
import * as TopNavigationStylesDr from './top-navigation.dr.module.scss';
import { getMultilinkURL } from '../../../lib/helpers/helpers';

const TopNavigation = ({ showCookieLayer, storyblokCommon }: { showCookieLayer: () => any; storyblokCommon: StoryblokCommon }) => {
    const accountContext = React.useContext(AccountContext);
    const [logoImage, setLogoImage] = React.useState<string | undefined>();
    const [activeSubMenu, _setActiveSubMenu] = React.useState<string>();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState<boolean>(false);
    const activeSubMenuRef = React.useRef(activeSubMenu);
    const isHSTheme = storyblokCommon.configuration.content.theme === 'hs';
    const TopNavigationStyles = storyblokCommon.configuration.content.theme === 'dr' ? TopNavigationStylesDr : TopNavigationStylesHs;
    const accountPageUrl = getMultilinkURL(storyblokCommon.topNavigation.content.account_link);

    import('../../../images/header/' + storyblokCommon.configuration.content.theme + '/logo.svg')
        .then((image) => {
            if (image?.default) {
                setLogoImage(image.default);
            }
        })
        .catch(() => {
            //
        });

    const setActiveSubMenu = (submenu: string) => {
        activeSubMenuRef.current = submenu;
        _setActiveSubMenu(submenu);
    };

    const handlePageClick = (e: MouseEvent) => {
        if (activeSubMenuRef.current) {
            if (e.target) {
                // @ts-ignore
                const targetId: string = e.target.id ?? '';
                // @ts-ignore
                const parentElementId: string = e.target.closest('ul')?.id ?? '';
                // if click was not on submenu-button and not inside submenu-layer: close layer
                if (!targetId.startsWith('sub-menu') && !parentElementId.startsWith('sub-menu')) {
                    setActiveSubMenu('');
                }
            }
        }
    };

    const toggleSubMenu = (text: string) => {
        setActiveSubMenu(activeSubMenu === text ? '' : text);
    };

    const toggleCookieLayer = (e: any) => {
        e.preventDefault();
        showCookieLayer();
    };

    const isActiveParent = (navEntry: NavigationLinkStoryblok | NavigationSubLinkStoryblok): boolean => {
        let result = false;
        if (typeof window !== 'undefined') {
            const pathname = window.location.pathname.substring(1);
            navEntry.links.map((subNavEntry: any) => {
                if (subNavEntry.link.cached_url === pathname) {
                    result = true;
                }
            });
        }
        return result;
    };

    const getNavigationEntries = (type: 'header' | 'footer'): React.ReactFragment => {
        const sbNavigation: (NavigationSubLinkStoryblok | NavigationLinkStoryblok | NavigationItemTopStoryblok)[] | undefined =
            type === 'header'
                ? storyblokCommon.topNavigation
                    ? _.cloneDeep(storyblokCommon.topNavigation.content.links)
                    : undefined
                : storyblokCommon.footer
                ? _.cloneDeep(storyblokCommon.footer.content.links)
                : undefined;

        if (sbNavigation) {
            // @ts-ignore
            return sbNavigation.map((navEntry: NavigationSubLinkStoryblok | NavigationLinkStoryblok | NavigationItemTopStoryblok) => {
                if (navEntry.component !== 'navigation_item_top') {
                    if (navEntry.privacy_settings) {
                        return (
                            <div key={navEntry._uid} className={type === 'footer' ? TopNavigationStyles.footerEntry : ''} role="none">
                                <a href="#" onClick={toggleCookieLayer} role="menuitem">
                                    {navEntry.text}
                                </a>
                            </div>
                        );
                    } else if (navEntry.link && (navEntry.link.url || navEntry.link.cached_url)) {
                        return (
                            <div key={navEntry._uid} className={type === 'footer' ? TopNavigationStyles.footerEntry : ''} role="none">
                                {getLinkFragment(navEntry, TopNavigationStyles.linkActiveBold)}
                            </div>
                        );
                    } else {
                        return (
                            <div key={navEntry._uid} className={type === 'footer' ? TopNavigationStyles.footerEntry : ''} role="none">
                                <button
                                    id={'sub-menu-button-' + navEntry._uid}
                                    onClick={() => toggleSubMenu(navEntry.text ?? '')}
                                    aria-haspopup="true"
                                    aria-controls={'sub-menu-' + navEntry._uid}
                                    aria-expanded={activeSubMenu === navEntry.text}
                                    className={[
                                        activeSubMenu === navEntry.text ? TopNavigationStyles.linkActive : '',
                                        isActiveParent(navEntry) ? TopNavigationStyles.linkActiveBold : '',
                                    ].join(' ')}
                                >
                                    {navEntry.text}
                                </button>
                                <span
                                    className={
                                        activeSubMenu === navEntry.text
                                            ? TopNavigationStyles.iconArrowDown
                                            : TopNavigationStyles.iconArrowUp
                                    }
                                />
                                <ul
                                    id={'sub-menu-' + navEntry._uid}
                                    role="menu"
                                    aria-labelledby={'sub-menu-button-' + navEntry._uid}
                                    className={activeSubMenu === navEntry.text ? TopNavigationStyles.subMenuOpen : ''}
                                >
                                    {navEntry.links.map((subNavEntry: any) => {
                                        return (
                                            <li key={subNavEntry._uid} role="none">
                                                {getLinkFragment(subNavEntry, TopNavigationStyles.linkActiveBold)}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    }
                }
            });
        } else {
            return <></>;
        }
    };

    const pressEscCheck = React.useCallback((event) => {
        if (event.keyCode === 27) {
            toggleSubMenu('');
        }
    }, []);

    const handleScroll = React.useCallback(() => {
        toggleSubMenu('');
    }, []);

    const handleResize = React.useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    React.useEffect(() => {
        if (typeof accountContext.error === 'string') {
            if (accountContext.error.includes('AADB2C90118')) {
                accountContext.forgotPassword(storyblokCommon.configuration.content.language, storyblokCommon.configuration.content.theme);
            }
            if (accountContext.error.includes('AADB2C90091')) {
                accountContext.updateToken();
            }
        }
    }, [accountContext.error]);

    React.useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'visible';
        }
    }, [isMobileMenuOpen]);

    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            document.addEventListener('keydown', pressEscCheck, false);
            window.addEventListener('scroll', handleScroll);
            document.addEventListener('mousedown', handlePageClick);
            window.addEventListener('resize', handleResize);

            return () => {
                document.removeEventListener('keydown', pressEscCheck, false);
                window.removeEventListener('scroll', handleScroll);
                document.removeEventListener('mousedown', handlePageClick);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, []);

    return (
        <Headroom>
            <header className={TopNavigationStyles.header}>
                <div>
                    {/*LOGO START*/}
                    <Link
                        to={getMultilinkURL(storyblokCommon.topNavigation.content.home_link)}
                        className={TopNavigationStyles.logo}
                        aria-label={''}
                    >
                        {logoImage && (
                            <img
                                src={logoImage}
                                alt={storyblokCommon.topNavigation.content.home_aria_label}
                                width={storyblokCommon.configuration.content.theme === 'hs' ? '285' : '176'}
                                height={storyblokCommon.configuration.content.theme === 'hs' ? '55' : '74'}
                            />
                        )}
                    </Link>
                    {/*LOGO END*/}

                    {/*NAVIGATION START*/}
                    <div
                        className={[
                            TopNavigationStyles.navigationWrapper,
                            isMobileMenuOpen ? TopNavigationStyles.mobileMenuIsOpen : '',
                        ].join(' ')}
                    >
                        <nav role="menu">
                            {getNavigationEntries('header')}
                            {/*CUSTOMER MENU START*/}
                            <div className={TopNavigationStyles.iconArrowDown2}>
                                <button
                                    id={'sub-menu-button-99'}
                                    onClick={() => toggleSubMenu('customer-account')}
                                    aria-haspopup="true"
                                    aria-controls={'sub-menu-99'}
                                    aria-expanded={activeSubMenu === 'customer-account'}
                                    className={activeSubMenu === 'customer-account' ? TopNavigationStyles.linkActive : ''}
                                >
                                    {isHSTheme ? (
                                        <>
                                            {accountContext.account ? (
                                                <span className={TopNavigationStyles.iconUser}>
                                                    {storyblokCommon.topNavigation.content.navigation_text}
                                                </span>
                                            ) : (
                                                <span className={TopNavigationStyles.iconKey}>
                                                    {storyblokCommon.topNavigation.content.navigation_text_log_in}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className={TopNavigationStyles.iconUser}>
                                            {storyblokCommon.topNavigation.content.navigation_text}
                                        </span>
                                    )}
                                </button>
                                <span
                                    className={
                                        activeSubMenu === 'customer-account'
                                            ? TopNavigationStyles.iconArrowDown
                                            : TopNavigationStyles.iconArrowUp
                                    }
                                />
                                <ul
                                    id={'sub-menu-99'}
                                    role="menu"
                                    aria-labelledby={'sub-menu-button-99'}
                                    className={[
                                        TopNavigationStyles.customerAccount,
                                        activeSubMenu === 'customer-account' ? TopNavigationStyles.subMenuOpen : '',
                                    ].join(' ')}
                                >
                                    {!accountContext.account ? (
                                        <>
                                            <li role="none">
                                                <button
                                                    onClick={() =>
                                                        accountContext.signIn(
                                                            storyblokCommon.configuration.content.language,
                                                            storyblokCommon.configuration.content.theme,
                                                        )
                                                    }
                                                    className={TopNavigationStyles.iconLogin}
                                                >
                                                    {storyblokCommon.topNavigation.content.log_in}
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() =>
                                                        accountContext.signUp(
                                                            storyblokCommon.configuration.content.language,
                                                            storyblokCommon.configuration.content.theme,
                                                        )
                                                    }
                                                >
                                                    {storyblokCommon.topNavigation.content.register_prefix && (
                                                        <>
                                                            <strong>{storyblokCommon.topNavigation.content.register_prefix}</strong>{' '}
                                                        </>
                                                    )}
                                                    {storyblokCommon.topNavigation.content.register}
                                                </button>
                                            </li>
                                            <li>
                                                <hr />
                                                <Link to={getMultilinkURL(storyblokCommon.topNavigation.content.register_advantages_link)}>
                                                    {storyblokCommon.topNavigation.content.register_advantages}
                                                </Link>
                                            </li>
                                        </>
                                    ) : (
                                        <>
                                            <li role="none">
                                                <Link
                                                    to={accountPageUrl}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(accountPageUrl, {
                                                            state: { pageId: 'Profile' },
                                                        });
                                                        toggleSubMenu('customer-account');
                                                    }}
                                                >
                                                    {storyblokCommon.configuration.content.account_navigation_profile}
                                                </Link>
                                            </li>
                                            <li role="none">
                                                <Link
                                                    to={accountPageUrl}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(accountPageUrl, {
                                                            state: { pageId: 'Addresses' },
                                                        });
                                                        toggleSubMenu('customer-account');
                                                    }}
                                                >
                                                    {storyblokCommon.configuration.content.account_navigation_addresses}
                                                </Link>
                                            </li>
                                            <li role="none">
                                                <Link
                                                    to={accountPageUrl}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(accountPageUrl, {
                                                            state: { pageId: 'Devices' },
                                                        });
                                                        toggleSubMenu('customer-account');
                                                    }}
                                                >
                                                    {storyblokCommon.configuration.content.account_navigation_devices}
                                                </Link>
                                            </li>
                                            {storyblokCommon.configuration.content.account_navigation_promocode && (
                                                <li role="none">
                                                    <Link
                                                        to={accountPageUrl}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            navigate(accountPageUrl, {
                                                                state: { pageId: 'Promocode' },
                                                            });
                                                            toggleSubMenu('customer-account');
                                                        }}
                                                    >
                                                        {storyblokCommon.configuration.content.account_navigation_promocode}
                                                    </Link>
                                                </li>
                                            )}

                                            {storyblokCommon.configuration.content.account_navigation_contract_data && (
                                                <li role="none">
                                                    <Link
                                                        to={accountPageUrl}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            navigate(accountPageUrl, {
                                                                state: { pageId: 'ContractData' },
                                                            });
                                                            toggleSubMenu('customer-account');
                                                        }}
                                                    >
                                                        {storyblokCommon.configuration.content.account_navigation_contract_data}
                                                    </Link>
                                                </li>
                                            )}

                                            {storyblokCommon.configuration.content.account_navigation_cost_estimates && (
                                                <li role="none">
                                                    <Link
                                                        to={accountPageUrl}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            navigate(accountPageUrl, {
                                                                state: { pageId: 'CostEstimates' },
                                                            });
                                                            toggleSubMenu('customer-account');
                                                        }}
                                                    >
                                                        {storyblokCommon.configuration.content.account_navigation_cost_estimates}
                                                    </Link>
                                                </li>
                                            )}

                                            <li role="none">
                                                <hr />
                                                <button onClick={accountContext.signOut}>
                                                    {storyblokCommon.topNavigation.content.log_out}
                                                </button>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            </div>
                            {/*CUSTOMER MENU END*/}
                            {getNavigationEntries('footer')}
                        </nav>
                    </div>
                    {/*NAVIGATION END*/}

                    {/*BURGER ICON START*/}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className={[TopNavigationStyles.mobileButton, isMobileMenuOpen ? TopNavigationStyles.isCloseButton : ''].join(' ')}
                        aria-label="Menu"
                    />
                    {/*BURGER ICON END*/}
                </div>
            </header>
        </Headroom>
    );
};

export default TopNavigation;
